import { useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "../api/client";
import { useSettings } from "../context/SettingsContext";

const MUSHROOM_SPECIES = [
  "Golden Teacher", "Penis Envy", "B+", "Albino A+", "Amazonian",
  "Blue Meanie", "Cambodia", "Ecuadorian", "Hawaiian", "Huautla",
  "Koh Samui", "Lipa Yai", "Mazatapec", "McKennaii", "Puerto Rican",
  "Transkei", "Tidal Wave", "Z-Strain"
];

export default function GuidedSessionPage() {
  const navigate = useNavigate();
  const { settings, loading } = useSettings();

  const [step, setStep] = useState("setup");
  const [subcategory, setSubcategory] = useState("");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("");
  const [sessionTiming, setSessionTiming] = useState("");
  const [intention, setIntention] = useState("");
  const [sitterName, setSitterName] = useState("");
  const [sitterEmail, setSitterEmail] = useState("");
  const [sitterNote, setSitterNote] = useState("");
  const [sitterType, setSitterType] = useState("remote");
  const [sitterLink, setSitterLink] = useState("");
  const [sitterToken, setSitterToken] = useState("");
  const [qrVisible, setQrVisible] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");


  const handleCreate = async () => {
    if (sitterType === "remote" && !sitterEmail) {
      setError("Please enter your sitter's email for a remote session.");
      return;
    }
    setCreating(true);
    setError("");
    try {
      const res = await client.post("/sitter/create", {
        entry_id: null,
        sitter_name: sitterName || null,
        sitter_email: sitterEmail || null,
        sitter_type: sitterType,
        user_note: sitterNote || null,
        medicine_type: "mushroom",
        subcategory: subcategory || null,
        amount: amount || null,
        method: method || null,
        session_timing: sessionTiming || null,
        intention: intention || null,
      });
      setSitterLink(res.data.sitter_link);
      setSitterToken(res.data.token);
      setStep("ready");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create session. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  if (step === "ready") {
    return (
      <main style={{ maxWidth: "520px", margin: "0 auto" }}>
        <h2 style={{ marginBottom: "6px" }}>🧭 Session Flow Ready</h2>
        <p style={{ opacity: 0.6, fontSize: "14px", marginBottom: "24px" }}>
          Share the link below with your companion before you begin.
        </p>

        <div style={{ background: "rgba(180,125,232,0.07)", border: "1px solid rgba(180,125,232,0.25)", borderRadius: "12px", padding: "20px", marginBottom: "16px" }}>
          {sitterEmail && (
            <p style={{ fontSize: "13px", color: "#d4b0ff", marginBottom: "10px", margin: "0 0 10px" }}>
              ✓ Link sent to {sitterEmail}
            </p>
          )}
          <input
            readOnly value={sitterLink} onFocus={e => e.target.select()}
            style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.04)", color: "inherit", marginBottom: "12px", fontSize: "12px", fontFamily: "monospace" }}
          />
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <button onClick={() => navigator.clipboard.writeText(sitterLink)} style={{ border: "none", borderRadius: "999px", padding: "8px 16px", background: "#7dd3e8", color: "#06131a", fontSize: "12px", fontWeight: 600, cursor: "pointer" }}>
              Copy Link
            </button>
            <button onClick={() => window.open(sitterLink, "_blank")} style={{ border: "none", borderRadius: "999px", padding: "8px 16px", background: "#b47de8", color: "white", fontSize: "12px", fontWeight: 600, cursor: "pointer" }}>
              Open Live Session
            </button>
            <button onClick={() => setQrVisible(v => !v)} style={{ border: "1px solid rgba(255,255,255,0.15)", borderRadius: "999px", padding: "8px 16px", background: "transparent", color: "inherit", fontSize: "12px", fontWeight: 600, cursor: "pointer" }}>
              {qrVisible ? "Hide QR" : "Show QR Code"}
            </button>
          </div>
          {qrVisible && (
            <img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(sitterLink)}`} alt="QR code" style={{ width: "180px", height: "180px", borderRadius: "10px", marginTop: "14px", display: "block" }} />
          )}
        </div>

        {/* JOURNALING GUIDANCE — replaces Write Journal Entry button */}
        <div style={{ padding: "14px 16px", borderRadius: "10px", border: "1px solid rgba(125,211,232,0.15)", background: "rgba(125,211,232,0.05)", marginBottom: "16px", fontSize: "13px", lineHeight: 1.6, opacity: 0.85 }}>
          📓 Once you open the live session, you can write journal entries at any time from within the session page.
        </div>

        <button onClick={() => navigate("/")} style={{ border: "1px solid rgba(255,255,255,0.15)", borderRadius: "999px", padding: "9px 18px", background: "transparent", color: "inherit", fontSize: "13px", cursor: "pointer" }}>
          ← Back to Journal
        </button>
      </main>
    );
  }

  return (
    <main style={{ maxWidth: "520px", margin: "0 auto" }}>
      <span onClick={() => navigate("/")} style={{ fontSize: "13px", opacity: 0.5, cursor: "pointer", display: "inline-block", marginBottom: "20px" }}>
        ← Back
      </span>

      <h2 style={{ marginBottom: "4px" }}>🧭 New Session Flow:</h2>
      <p style={{ opacity: 0.75, fontSize: "14px", marginBottom: "24px" }}>
        Set up a live session for your companion. They will receive a private link to support you in real time.
      </p>
      {!loading && (
        !settings.preserve_chat_history ? (
          <p style={{ opacity: 0.75, fontSize: "14px", marginBottom: "24px" }}>
            <i>Do you wish to save chat history with your companion? You currently have Chat History disabled, but you can{" "}
            <a
              href="/account"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#7dd3e8", textDecoration: "underline" }}
            >
              enable it in Account
            </a>.
            </i>
          </p>
        ) : (
          <p style={{ opacity: 0.75, fontSize: "14px", marginBottom: "24px" }}><i>You are saving chat history with this sitter.</i></p>
        )
      )}

      {/* SESSION DETAILS */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", marginBottom: "14px" }}>
        <label>
          <span style={{ fontSize: "12px", opacity: 0.6, display: "block", marginBottom: "4px" }}>Type</span>
          <select value={subcategory} onChange={e => setSubcategory(e.target.value)}>
            <option value="">-- Select Type --</option>
            {MUSHROOM_SPECIES.map(s => <option key={s} value={s}>{s}</option>)}
            <option value="other">Other / Custom</option>
          </select>
        </label>

        <label>
          <span style={{ fontSize: "12px", opacity: 0.6, display: "block", marginBottom: "4px" }}>Dose</span>
          <input type="text" value={amount} onChange={e => setAmount(e.target.value)} placeholder="e.g. 2.5g" />
        </label>

        <label>
          <span style={{ fontSize: "12px", opacity: 0.6, display: "block", marginBottom: "4px" }}>Form</span>
          <select value={method} onChange={e => setMethod(e.target.value)}>
            <option value="">-- Select --</option>
            {["Dried", "Tea", "Lemon Tek", "Capsule", "Chocolate", "Gummy", "Tincture", "Other"].map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </label>

        <label>
          <span style={{ fontSize: "12px", opacity: 0.6, display: "block", marginBottom: "4px" }}>Set & Setting</span>
          <input type="text" value={sessionTiming} onChange={e => setSessionTiming(e.target.value)} placeholder="e.g. forest, home, ceremony..." />
        </label>
      </div>

      <label style={{ marginBottom: "20px", display: "block" }}>
        <span style={{ fontSize: "12px", opacity: 0.6, display: "block", marginBottom: "4px" }}>Intention (optional)</span>
        <textarea
          value={intention}
          onChange={e => setIntention(e.target.value)}
          placeholder="What are you bringing into this journey?"
          rows={3}
          style={{ width: "100%", boxSizing: "border-box", resize: "vertical" }}
        />
      </label>

      {/* COMPANION SETUP */}
      <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "20px", marginBottom: "20px" }}>
        <div style={{ fontSize: "13px", fontWeight: 600, marginBottom: "12px" }}>Will a companion be with you?</div>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "16px" }}>
          {[
            { value: "inperson", label: "Yes, in person" },
            { value: "remote", label: "Yes, remotely" },
            { value: "solo", label: "Going solo" },
          ].map(opt => (
            <label key={opt.value} style={{
              display: "inline-flex", alignItems: "center", gap: "7px",
              fontSize: "13px", cursor: "pointer", padding: "7px 12px", borderRadius: "999px",
              background: sitterType === opt.value ? "rgba(255,255,255,0.06)" : "transparent",
              border: sitterType === opt.value ? "1px solid rgba(255,255,255,0.12)" : "1px solid transparent",
            }}>
              <input type="radio" name="sitterType" value={opt.value} checked={sitterType === opt.value} onChange={() => setSitterType(opt.value)} style={{ margin: 0 }} />
              {opt.label}
            </label>
          ))}
        </div>

        {sitterType !== "solo" && (
          <div style={{ display: "grid", gap: "10px" }}>
            <input type="text" value={sitterName} onChange={e => setSitterName(e.target.value)} placeholder="Companion name (optional)" />
            <input type="email" value={sitterEmail} onChange={e => setSitterEmail(e.target.value)} placeholder={sitterType === "remote" ? "Companion email — we will send them the link" : "Companion email (optional)"} />
            <textarea
              value={sitterNote}
              onChange={e => setSitterNote(e.target.value)}
              placeholder="Write a note your companion will see."
              rows={3}
              style={{ resize: "vertical" }}
            />
          </div>
        )}
      </div>

      {error && <p style={{ color: "#ff8c8c", fontSize: "13px", marginBottom: "12px" }}>{error}</p>}

      <button
  onClick={() => {
    if (sitterType === "solo") {
      navigate(
        `/new?medicine=mushroom` +
        `&subcategory=${encodeURIComponent(subcategory)}` +
        `&method=${encodeURIComponent(method)}` +
        `&amount=${encodeURIComponent(amount)}` +
        `&session_timing=${encodeURIComponent(sessionTiming)}` +
        `&intention=${encodeURIComponent(intention)}`
      );
    } else {
      handleCreate();
    }
  }}
  disabled={creating}
  style={{
    border: "none",
    background: "#9f5ae0",
    color: "#fff",
    cursor: creating ? "wait" : "pointer",
    opacity: creating ? 0.7 : 1,
  }}
>
  {creating ? "Setting up..." : sitterType === "solo" ? "Start Journey →" : "Start Session Flow →"}
</button>
    </main>
  );
}
